const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/uster/eCheckin/sections/PersAngaben.tsx';
import React, { forwardRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Divider } from '@mui/material';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { actionRemoveFromCIS4mDataObject } from '@cistec/common_web/modules/CIS4m/actions';
import { useCIS4mContext } from '@cistec/common_web/modules/CIS4m/context';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { CIS4mSingleCheckbox } from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import textMapCommon from '../../../common/translations';
import textMap from 'App/config/uster/eCheckin/translations';
import { createFieldPath } from 'App/components/eCheckin/utils';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { GenderField } from 'App/components/eCheckin/components/fields/metakat/GenderField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { validateAHVNoDots } from '@cistec/common_web/components/Forms/utils/validation';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const PATH_GENDER = createFieldPath(path, 'gender');
    const PATH_FIRST_NAME = createFieldPath(path, 'firstname');
    const PATH_LAST_NAME = createFieldPath(path, 'lastname');
    const PATH_LEDIGER_NAME = createFieldPath(path, 'maidenname');
    const PATH_DATE_OF_BIRTH = createFieldPath(path, 'dateOfBirth');
    const PATH_NATIONALITY = createFieldPath(path, 'nationality');
    const PATH_HOME_TOWN = createFieldPath(path, 'hometown');

    const PATH_TAX_DOMICILE = createFieldPath(path, 'taxDomicile');
    const PATH_TAX_DOMICILE_ADRESS = createFieldPath(path, 'taxDomicileAdress');

    const PATH_AHV = createFieldPath(path, 'ahv');

    const PATH_TEL = createFieldPath(path, 'tel');
    const PATH_MOBILE = createFieldPath(path, 'mobile');
    const PATH_COUNTRY = createFieldPath(path, 'country');
    const PATH_EMAIL = createFieldPath(path, 'email');
    const PATH_NO_EMAIL = createFieldPath(path, 'noEmail');
    const PATH_RELIGION = createFieldPath(path, 'religion');
    const PATH_CIVILSTANTUS = createFieldPath(path, 'civilStatus');
    const PATH_PROFESSION = createFieldPath(path, 'profession');

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();
    const { formatMessage } = useIntl();

    const hiddenTaxDomicile = !_.get(formData, PATH_TAX_DOMICILE);
    const noEmail = _.get(formData, PATH_NO_EMAIL) === '1';

    const dispatch = useDispatch<any>();
    const { formId } = useCIS4mContext();

    useEffect(() => {
        if (noEmail) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_EMAIL));
        }
    }, [noEmail]);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <GenderField path={PATH_GENDER} label={textMap.gender} required={true} />
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.maidenname} path={PATH_LEDIGER_NAME} />
            <AddressBlock basePath={path} fieldsConfig={addressFieldsConfig} required={true} />
            <BirthDateField label={textMap.dateOfBirth} path={PATH_DATE_OF_BIRTH} required={true} />
            <AHVField
                label={textMap.ahv}
                path={PATH_AHV}
                required={true}
                mask={'7569999999999'}
                checkValidity={validateAHVNoDots}
            />
            <CountryField label={textMap.nationality} path={PATH_NATIONALITY} required={true} />
            <CIS4mTextField label={textMap.hometown} path={PATH_HOME_TOWN} required={true} />

            <CIS4mSingleCheckbox label={textMap.taxDomicile} path={PATH_TAX_DOMICILE} />
            <CIS4mBlock hidden={hiddenTaxDomicile}>
                <CIS4mTextField label={textMap.taxDomicileAdress} path={PATH_TAX_DOMICILE_ADRESS} required={true} />
                <Divider />
            </CIS4mBlock>
            <PhoneNumberField label={textMapCommon.tel} path={PATH_TEL} countryCodePath={PATH_COUNTRY} />
            <PhoneNumberField
                label={textMapCommon.mobile}
                path={PATH_MOBILE}
                required={true}
                countryCodePath={PATH_COUNTRY}
            />
            <EMailField label={textMapCommon.email} path={PATH_EMAIL} required={noEmail ? false : true} />
            <CIS4mSingleCheckbox label={textMap.noEmail} path={PATH_NO_EMAIL} />
            {noEmail && <div>{formatMessage(textMap.noEmailText)}</div>}
            <div>{formatMessage(textMap.noMarketing)}</div>
            <Divider />
            <ReligionField label={textMap.religion} path={PATH_RELIGION} />
            <ZivilstandField label={textMap.civilStatus} path={PATH_CIVILSTANTUS} required={true} />
            <CIS4mTextField label={textMap.profession} path={PATH_PROFESSION} />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
